export type * from "@elaso/db";

import type { Prisma } from "@elaso/db";

export type CompleteMessage = Prisma.MessageGetPayload<{
	include: {
		reactions: true;
		referencedMessage: true;
		attachments: true;
	};
}> & {
	isPending?: boolean;
	hasFailed?: boolean;
	tempId?: string;
};

export type ActivityStatus = "active" | "idle";

/**
 * Presence reaches every connection in the workspace several times a second, so
 * a field nothing reads is paid for on every flush. Two were dropped in
 * TECH-3157. `id` duplicated `firebaseUid`, and the client's nested-key config
 * keys on `firebaseUid`, so nothing read it. `lastActivityTime` was never read,
 * because the client's same-named field is a local number for idle detection,
 * populated from `Date.now()` rather than from this payload.
 *
 * `lastChatTime` looks equally dead and is NOT. The attendance table's last-chat
 * column falls back to it when no attendance data is loaded. Removing it blanks
 * that column. See TECH-3157 finding 3 for the ordering that change needs.
 *
 * The offline path still sends `{ id: firebaseUid }`, but as a `remove` payload
 * where `id` is the map key the client deletes by, unrelated to the field
 * removed here.
 */
export type UserPresence = {
	firebaseUid: string;
	status: "online" | "offline";
	activityStatus?: ActivityStatus; // active = user interacting, idle = no interaction for 5 min
	currentChannelId?: string | null; // Channel ID the user is currently in
	lastChatTime?: string | null; // ISO timestamp of last chat message sent
	isMicrophoneEnabled?: boolean; // Whether user's microphone is enabled in voice channel
	isCameraEnabled?: boolean; // Whether user's camera is enabled in voice channel
	profile?: import("../schemas/profile").UserProfile; // Included in presence messages so clients can populate user entries without waiting for users.list
};

export type AttendanceSessionStatus = "pending" | "active" | "completed";

export type ChannelType = "text" | "voice_and_text" | "stage";

export type AttendanceSession = {
	id: string;
	name: string;
	status: AttendanceSessionStatus;
	createdBy: string;
	startedAt: string | null;
	endedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

export type Category = {
	id: string;
	name: string;
	workspaceId: string;
	order: number;
	isVisible: boolean;
	maxParticipants: number | null;
	createdAt: Date;
	updatedAt: Date;
};

export type WorkspaceScheduleSettings = {
	scheduleTitle: string | null;
	fullScheduleRoleKeys: string[];
};

export const DEFAULT_WORKSPACE_SCHEDULE_SETTINGS: WorkspaceScheduleSettings = {
	scheduleTitle: "Schedule",
	fullScheduleRoleKeys: [],
};

export type MuteDuration = "15m" | "1h" | "8h" | "24h" | "indefinite";
